import { useQuery, useQueryClient } from '@tanstack/react-query'
import { grantApi } from '../lib/api'
import { writeGrantMirror, clearGrantMirror } from '../lib/grantMirror'
import { clearOrganizerCanvass } from '../lib/organizerCanvass'
import { clearReturnCredential } from '../lib/returnCredential'
import { wipeOfflineData } from '../lib/offlineStore'
import { syncEngine } from '../lib/syncEngine'
import { clearCredential } from '../lib/native/credentialStore'
import { isNativeApp } from '../lib/native/platform'

/** [Native B1] Upper bound on how long a native logout waits for the best-effort wipe-ack. */
export const WIPE_ACK_TIMEOUT_MS = 3000

/** Resolves when `p` settles or after `ms`, whichever is first; never rejects. */
function settleWithin(p: Promise<void>, ms: number): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined
  const timeout = new Promise<void>((resolve) => { timer = setTimeout(resolve, ms) })
  return Promise.race([p, timeout]).finally(() => clearTimeout(timer))
}
import type { GrantIdentity } from '../types/grant'

export function useGrantSession() {
  const qc = useQueryClient()

  const query = useQuery<GrantIdentity>({
    queryKey: ['grant', 'me'],
    queryFn: async () => {
      const { data } = await grantApi.me()
      writeGrantMirror({
        grantId: data.grantId,
        kind: data.kind,
        subjectContactId: data.subjectContactId,
        ownerCommitteeId: data.ownerCommitteeId,
        campaign: data.campaign ?? null,
        poc: data.poc ?? null,
      })
      return data
    },
    retry: false,
    staleTime: 5 * 60 * 1000,
  })

  const logout = async (): Promise<{ revokeFailed: boolean; wipeFailed: boolean }> => {
    // 1. Best-effort flush of unsynced visits WHILE the session is still live (spec §3.2).
    //    A server-driven revoke can't flush — that path blunt-wipes at the signed-out screen.
    if (navigator.onLine) {
      try {
        await syncEngine.sync()
      } catch {
        // best-effort — the [LEGAL] wipe below still runs
      }
    }
    // 2. Revoke the server-side session, with one silent retry.
    let revokeFailed = false
    try {
      await grantApi.logout()
    } catch {
      try {
        await grantApi.logout()
      } catch (err) {
        revokeFailed = true
        // [telemetry] No prod error pipeline exists (spec §3.4); a structured console.error is
        // the available operator sink. The organizer-visible signal is 1e's session dashboard.
        console.error('[grant] logout revoke POST failed after retry; session may persist server-side until expiry', err)
      }
    }
    // 3. [LEGAL] wipe cached PII — ALWAYS attempted. Guarded (not swallowed): a rejection
    //    must not throw past logout (the caller still needs to reach the signed-out screen),
    //    but it also must not go silent — log it and surface it in the returned result so the
    //    session dashboard / Profile can show it (§E: guard-and-surface, don't swallow).
    let wipeFailed = false
    try {
      await wipeOfflineData()
      // [LEGAL] Confirm the wipe to the server so the disclosure record's "Wipe confirmed" is real.
      // Best-effort: on self-logout the revoke above already cleared the cookie, so this typically
      // no-ops server-side (WipeRequestedAt was stamped there); it still fires for symmetry with the
      // signed-out landing, where the cookie survives a server-driven revoke.
      const ack = grantApi.wipeAck().then(() => {}, () => {})
      // [Native B1, Ruling R18] On native the ack must leave WITH the gs_ bearer, so it is awaited
      // (bounded) before the credential is cleared below — the request interceptor reads it in a
      // later microtask. Web stays fire-and-forget (the cookie rides regardless).
      if (isNativeApp()) await settleWithin(ack, WIPE_ACK_TIMEOUT_MS)
    } catch (err) {
      wipeFailed = true
      // No prod error pipeline exists (spec §3.4); a structured console.error is the
      // available operator sink. Never log PII here — failure + error only.
      console.error('[LEGAL] offline wipe failed on logout', err)
    }
    clearGrantMirror()
    clearOrganizerCanvass() // [Canvass this] the marker must not outlive the grant session
    clearReturnCredential() // [L11 P2] deliberate sign-off drops the re-entry credential too
    // [Native B1] Only now — after the revoke and wipe above have authenticated with it — drop the
    // gs_ credential and (awaited, not fire-and-forget) the re-entry credential. No-ops on web.
    await clearCredential('grant').catch(() => {})
    await clearCredential('canvasserReturn').catch(() => {})
    qc.removeQueries({ queryKey: ['grant', 'me'] })
    qc.removeQueries({ queryKey: ['grant', 'profile'] }) // [Phase 7] holds the canvasser's own phone/email
    qc.removeQueries({ queryKey: ['grant', 'impact'] }) // [Phase 8] team counts belong to this session only
    return { revokeFailed, wipeFailed }
  }

  return {
    identity: query.data ?? null,
    isAuthed: !!query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error as unknown,
    refresh: query.refetch,
    logout,
  }
}
